/**
 * useOrderListFilters — URL-canonical order list filter state with localStorage
 * rehydration when the sidebar remounts `/orders` without query params (#58229).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  isOrderCreateDrawerOpen,
  preserveOrderCreateDrawerParams,
} from '../lib/order-create-drawer-url';
import { pruneOrderListStatusForView } from '../lib/order-list-status-options';
import { nextOrderListSortParam, parseOrderListSortParam } from '../lib/order-list-sort';
import type { OrderColumnKey } from '../lib/orderColumns';
import type { OrderListInfiniteParams } from './useOrders';

export type OrderListView = 'active' | 'inactive' | 'all';

/** First-load tab. Legacy `filters.component.ts` uses All unless `filterStatus=active`. */
export const DEFAULT_ORDER_LIST_VIEW: OrderListView = 'all';

function isOrderListView(value: unknown): value is OrderListView {
  return value === 'active' || value === 'inactive' || value === 'all';
}

/** Parsed filter state for the order list (arrays for multi-select UI). */
export type OrderListFilterState = {
  view: OrderListView;
  q?: string;
  status?: string[];
  serviceType?: string[];
  companyIds?: number[];
  assignedToUserIds?: number[];
  priority?: number[];
  subscription?: number[];
  dateFrom?: string;
  dateTo?: string;
};

const STORAGE_KEY_PREFIX = 'orders-list-filters-v1';
const SEARCH_DEBOUNCE_MS = 300;
const MAX_SELECTIONS = 50;

const FILTER_PARAM_KEYS = [
  'view',
  'q',
  'status',
  'serviceType',
  'companyIds',
  'assignedToUserIds',
  'priority',
  'subscription',
  'dateFrom',
  'dateTo',
] as const;

const SSI_ONLY_FILTER_KEYS = ['companyIds', 'assignedToUserIds', 'priority', 'subscription'] as const;

export const DEFAULT_ORDER_LIST_FILTERS: OrderListFilterState = {
  view: DEFAULT_ORDER_LIST_VIEW,
};

import { isSsiStaffRole } from '../lib/order-ssi-roles';

/** Whether the caller can use SSI-only list filters (matches server isSsi). */
export function isSsiOrderListRole(role: string | undefined): boolean {
  return isSsiStaffRole(role);
}

function parseCsvStrings(raw: string | null): string[] | undefined {
  if (!raw?.trim()) return undefined;
  const values = raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, MAX_SELECTIONS);
  return values.length ? values : undefined;
}

function parseCsvPositiveInts(raw: string | null): number[] | undefined {
  if (!raw?.trim()) return undefined;
  const values = raw
    .split(',')
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0)
    .slice(0, MAX_SELECTIONS);
  return values.length ? values : undefined;
}

/** Legacy Assigned To filter sentinel (`filters.component.ts` prepends `userId: 0`). */
export const UNASSIGNED_ASSIGNEE_USER_ID = 0;

/** Assigned-to URL param: positive staff ids plus the Unassigned sentinel `0`. */
function parseCsvAssignedToUserIds(raw: string | null): number[] | undefined {
  if (!raw?.trim()) return undefined;
  const values = raw
    .split(',')
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((n) => Number.isFinite(n) && n >= UNASSIGNED_ASSIGNEE_USER_ID)
    .slice(0, MAX_SELECTIONS);
  return values.length ? values : undefined;
}

function serializeCsvStrings(values: string[] | undefined): string | undefined {
  if (!values?.length) return undefined;
  return values.join(',');
}

function serializeCsvInts(values: number[] | undefined): string | undefined {
  if (!values?.length) return undefined;
  return values.join(',');
}

/** True when the URL carries any list-filter query param (shared links win over localStorage). */
export function urlHasOrderListFilterParams(params: URLSearchParams): boolean {
  for (const key of FILTER_PARAM_KEYS) {
    const value = params.get(key);
    if (value != null && value !== '') return true;
  }
  return false;
}

/** Parse URL search params into typed filter state. */
export function parseOrderListFiltersFromSearchParams(
  params: URLSearchParams,
): OrderListFilterState {
  const viewRaw = params.get('view');
  const view: OrderListView = isOrderListView(viewRaw) ? viewRaw : DEFAULT_ORDER_LIST_VIEW;

  const q = params.get('q')?.trim() || undefined;

  return {
    view,
    q,
    status: parseCsvStrings(params.get('status')),
    serviceType: parseCsvStrings(params.get('serviceType')),
    companyIds: parseCsvPositiveInts(params.get('companyIds')),
    assignedToUserIds: parseCsvAssignedToUserIds(params.get('assignedToUserIds')),
    priority: parseCsvPositiveInts(params.get('priority')),
    subscription: parseCsvPositiveInts(params.get('subscription')),
    dateFrom: params.get('dateFrom')?.trim() || undefined,
    dateTo: params.get('dateTo')?.trim() || undefined,
  };
}

/** Serialize filter state to URL search params (omits empty/default fields). Sort is merged separately. */
export function serializeOrderListFiltersToSearchParams(
  state: OrderListFilterState,
  sort?: string | null,
): URLSearchParams {
  const params = new URLSearchParams();
  if (state.view !== DEFAULT_ORDER_LIST_VIEW) params.set('view', state.view);
  if (state.q?.trim()) params.set('q', state.q.trim());
  const status = serializeCsvStrings(state.status);
  if (status) params.set('status', status);
  const serviceType = serializeCsvStrings(state.serviceType);
  if (serviceType) params.set('serviceType', serviceType);
  const companyIds = serializeCsvInts(state.companyIds);
  if (companyIds) params.set('companyIds', companyIds);
  const assignedToUserIds = serializeCsvInts(state.assignedToUserIds);
  if (assignedToUserIds) params.set('assignedToUserIds', assignedToUserIds);
  const priority = serializeCsvInts(state.priority);
  if (priority) params.set('priority', priority);
  const subscription = serializeCsvInts(state.subscription);
  if (subscription) params.set('subscription', subscription);
  if (state.dateFrom) params.set('dateFrom', state.dateFrom);
  if (state.dateTo) params.set('dateTo', state.dateTo);
  if (sort?.trim()) params.set('sort', sort.trim());
  return params;
}

/** Drop SSI-only filter fields for non-SSI sessions during rehydration / display. */
export function stripSsiOnlyOrderListFilters(
  state: OrderListFilterState,
  isSsi: boolean,
): OrderListFilterState {
  if (isSsi) return state;
  const next = { ...state };
  for (const key of SSI_ONLY_FILTER_KEYS) {
    delete next[key];
  }
  return next;
}

/** Map UI filter state to the list/export API query shape. */
export function orderListFiltersToApiParams(
  state: OrderListFilterState,
): OrderListInfiniteParams {
  return {
    view: state.view,
    search: state.q?.trim() || undefined,
    status: serializeCsvStrings(state.status),
    serviceType: serializeCsvStrings(state.serviceType),
    companyIds: serializeCsvInts(state.companyIds),
    assignedToUserIds: serializeCsvInts(state.assignedToUserIds),
    priority: serializeCsvInts(state.priority),
    subscription: serializeCsvInts(state.subscription),
    dateFrom: state.dateFrom,
    dateTo: state.dateTo,
  };
}

/** Whether any non-default filter is applied (for counter bar + empty states). */
export function hasActiveOrderListFilters(state: OrderListFilterState): boolean {
  if (state.view !== DEFAULT_ORDER_LIST_VIEW) return true;
  if (state.q?.trim()) return true;
  if (state.status?.length) return true;
  if (state.serviceType?.length) return true;
  if (state.companyIds?.length) return true;
  if (state.assignedToUserIds?.length) return true;
  if (state.priority?.length) return true;
  if (state.subscription?.length) return true;
  if (state.dateFrom) return true;
  if (state.dateTo) return true;
  return false;
}

function storageKey(userId: number): string {
  return userId > 0 ? `${STORAGE_KEY_PREFIX}:${userId}` : STORAGE_KEY_PREFIX;
}

/** Read persisted filters for a user; strips SSI-only fields when caller is not SSI. */
export function readPersistedOrderListFilters(
  userId: number,
  isSsi: boolean,
): OrderListFilterState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const blob = parsed as Record<string, unknown>;
    const state: OrderListFilterState = {
      view: isOrderListView(blob.view) ? blob.view : DEFAULT_ORDER_LIST_VIEW,
      q: typeof blob.q === 'string' ? blob.q : undefined,
      status: Array.isArray(blob.status)
        ? blob.status.filter((v): v is string => typeof v === 'string')
        : undefined,
      serviceType: Array.isArray(blob.serviceType)
        ? blob.serviceType.filter((v): v is string => typeof v === 'string')
        : undefined,
      companyIds: Array.isArray(blob.companyIds)
        ? blob.companyIds.filter((v): v is number => typeof v === 'number' && v > 0)
        : undefined,
      assignedToUserIds: Array.isArray(blob.assignedToUserIds)
        ? blob.assignedToUserIds.filter(
            (v): v is number => typeof v === 'number' && v >= UNASSIGNED_ASSIGNEE_USER_ID,
          )
        : undefined,
      priority: Array.isArray(blob.priority)
        ? blob.priority.filter((v): v is number => typeof v === 'number' && v > 0)
        : undefined,
      subscription: Array.isArray(blob.subscription)
        ? blob.subscription.filter((v): v is number => typeof v === 'number' && v > 0)
        : undefined,
      dateFrom: typeof blob.dateFrom === 'string' ? blob.dateFrom : undefined,
      dateTo: typeof blob.dateTo === 'string' ? blob.dateTo : undefined,
    };
    return stripSsiOnlyOrderListFilters(state, isSsi);
  } catch {
    return null;
  }
}

export function writePersistedOrderListFilters(userId: number, state: OrderListFilterState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    // Quota or private mode — persistence is best-effort UX, not a security boundary.
  }
}

export function clearPersistedOrderListFilters(userId: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    // ignore
  }
}

/** Merge a filter patch into live URL search params (caller supplies `prev`). */
export function mergeOrderListFilterPatch(
  prev: URLSearchParams,
  patch: Partial<OrderListFilterState>,
  isSsi: boolean,
): OrderListFilterState {
  const base = parseOrderListFiltersFromSearchParams(prev);
  return stripSsiOnlyOrderListFilters({ ...base, ...patch }, isSsi);
}

/**
 * True when the URL carries filter params but omitted a non-default `view` that
 * localStorage still holds — e.g. `?q=seattle` without `view=active` after sidebar return.
 */
export function orderListFiltersNeedViewRehydration(
  searchParams: URLSearchParams,
  current: OrderListFilterState,
  persisted: OrderListFilterState,
): boolean {
  return (
    !searchParams.has('view')
    && persisted.view !== DEFAULT_ORDER_LIST_VIEW
    && current.view === DEFAULT_ORDER_LIST_VIEW
  );
}

/**
 * Avoid clobbering a non-default persisted view when the URL omits `view` (All
 * is the wire default and is not serialized).
 */
export function filtersForOrderListPersistence(
  filters: OrderListFilterState,
  searchParams: URLSearchParams,
  userId: number,
  isSsi: boolean,
): OrderListFilterState {
  if (!searchParams.has('view') && filters.view === DEFAULT_ORDER_LIST_VIEW) {
    const existing = readPersistedOrderListFilters(userId, isSsi);
    if (existing?.view && existing.view !== DEFAULT_ORDER_LIST_VIEW) {
      return { ...filters, view: existing.view };
    }
  }
  return filters;
}

/**
 * Order list filters: URL is canonical; localStorage rehydrates empty mounts.
 * Search (`q`) debounces 300ms before committing to the URL + query key.
 */
export function useOrderListFilters() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = useAuthStore((s) => s.user?.userId ?? 0);
  const role = useAuthStore((s) => s.user?.role);
  const isSsi = isSsiOrderListRole(role);

  // Sidebar nav-away-and-return: rehydrate from localStorage when the list route
  // mounts with no filter query params, or when the URL is a partial subset (e.g.
  // `?q=` without `view=active`). Wait for auth userId so reads use the scoped key.
  // `location.key` ensures this runs on every in-app navigation back to `/orders`.
  useEffect(() => {
    if (!location.pathname.endsWith('/orders') || location.pathname.includes('/orders/')) {
      return;
    }
    if (userId <= 0) return;

    const persisted = readPersistedOrderListFilters(userId, isSsi);
    if (!persisted) return;

    const sort = searchParams.get('sort');
    const current = stripSsiOnlyOrderListFilters(
      parseOrderListFiltersFromSearchParams(searchParams),
      isSsi,
    );

    if (!urlHasOrderListFilterParams(searchParams) && !isOrderCreateDrawerOpen(searchParams)) {
      const next = preserveOrderCreateDrawerParams(
        serializeOrderListFiltersToSearchParams(persisted, sort),
        searchParams,
      );
      setSearchParams(next, { replace: true });
      return;
    }

    if (orderListFiltersNeedViewRehydration(searchParams, current, persisted)) {
      const merged = { ...current, view: persisted.view };
      const next = preserveOrderCreateDrawerParams(
        serializeOrderListFiltersToSearchParams(merged, sort),
        searchParams,
      );
      setSearchParams(next, { replace: true });
    }
  }, [location.key, location.pathname, searchParams, setSearchParams, userId, isSsi]);

  const filters = useMemo(
    () => stripSsiOnlyOrderListFilters(parseOrderListFiltersFromSearchParams(searchParams), isSsi),
    [searchParams, isSsi],
  );

  const apiParams = useMemo(() => {
    const sort = searchParams.get('sort')?.trim() || undefined;
    return {
      ...orderListFiltersToApiParams(filters),
      ...(sort ? { sort } : {}),
    };
  }, [filters, searchParams]);

  const listSort = useMemo(
    () => parseOrderListSortParam(searchParams.get('sort')),
    [searchParams],
  );

  const [searchInput, setSearchInputState] = useState(() => filters.q ?? '');
  const searchEditedRef = useRef(false);
  const debouncedSearchInput = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  const setSearchInput = useCallback((value: string) => {
    searchEditedRef.current = true;
    setSearchInputState(value);
  }, []);

  // Keep the input in sync when filters change externally (clear, back, rehydrate).
  // Resyncing the controlled input to the canonical URL value is the intended
  // effect here; the ref guard prevents clobbering in-flight user typing.
  useEffect(() => {
    if (searchEditedRef.current) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing controlled input to external URL state
    setSearchInputState(filters.q ?? '');
  }, [filters.q]);

  // Persistence happens at explicit commit time only (`setFilters` / `clearFilters`),
  // NOT via a reactive effect mirroring `filters → localStorage`. A reactive mirror
  // races the rehydration effect on sidebar return: a transient render where the URL
  // carries `?q=` but not yet `view=active` would persist a downgraded `view:'all'`
  // blob, which rehydration then restores — silently dropping the view tab (#58231
  // sticky regression). Commit-time writes carry the authoritative next state.

  const setFilters = useCallback(
    (patch: Partial<OrderListFilterState>) => {
      // Merge the patch into the LIVE URL params via the functional updater so
      // a concurrent rehydration or sort change can't be clobbered by a stale
      // `filters` snapshot (the cross-story #58229 sticky-filter regression).
      setSearchParams(
        (prev) => {
          const next = mergeOrderListFilterPatch(prev, patch, isSsi);
          if (userId > 0) {
            // Preserve a stored non-default `view` when this commit lands against a
            // URL that omits `view` (e.g. a debounced search commit racing the
            // sidebar-return rehydration). Without this, `{q}` against an empty URL
            // would persist `view:'all'` and drop the saved tab (#58231 sticky).
            writePersistedOrderListFilters(
              userId,
              filtersForOrderListPersistence(next, prev, userId, isSsi),
            );
          }
          return serializeOrderListFiltersToSearchParams(next, prev.get('sort'));
        },
        { replace: true },
      );
    },
    [isSsi, setSearchParams, userId],
  );

  // Debounced search commits trimmed text only after the raw input stabilizes.
  useEffect(() => {
    if (!searchEditedRef.current) return;
    if (debouncedSearchInput !== searchInput) return;
    const trimmed = searchInput.trim();
    const committed = filters.q?.trim() ?? '';
    if (trimmed === committed) return;
    setFilters({ q: trimmed || undefined });
  }, [debouncedSearchInput, searchInput, filters.q, setFilters]);

  const clearFilters = useCallback(() => {
    clearPersistedOrderListFilters(userId);
    searchEditedRef.current = false;
    setSearchInputState('');
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams, userId]);

  const setView = useCallback(
    (view: OrderListView) => {
      setFilters({
        view,
        status: pruneOrderListStatusForView(view, filters.status, isSsi),
      });
    },
    [filters.status, isSsi, setFilters],
  );

  const toggleSort = useCallback(
    (column: OrderColumnKey) => {
      // Only the `sort` param changes — merge into the live URL so the active
      // filters/view are preserved regardless of a stale render snapshot.
      setSearchParams(
        (prev) => {
          const nextSort = nextOrderListSortParam(prev.get('sort'), column);
          const base = stripSsiOnlyOrderListFilters(
            parseOrderListFiltersFromSearchParams(prev),
            isSsi,
          );
          return serializeOrderListFiltersToSearchParams(base, nextSort);
        },
        { replace: true },
      );
    },
    [isSsi, setSearchParams],
  );

  const hasActiveFilters = useMemo(() => hasActiveOrderListFilters(filters), [filters]);

  return {
    filters,
    apiParams,
    listSort,
    toggleSort,
    searchInput,
    setSearchInput,
    setView,
    setFilters,
    clearFilters,
    hasActiveFilters,
    isSsi,
  };
}
